import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { Image, Product } from 'src/typings';
import { environment } from 'src/environments/environment';
import { MatMenuTrigger } from '@angular/material/menu';
import { ProductService } from '../product.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddImageComponent } from '../add-image/add-image.component';
import { firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import {
  PartPhotoViewerData,
  PartPhotoViewerDialogComponent
} from '../../new-catalog/products/part-info/part-photo-viewer-dialog.component';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss']
})
export class GalleryComponent implements OnDestroy {
  constructor(private productService: ProductService, private dialog: MatDialog) {}

  @Input() set product(payload: Product | undefined) {
    const sameProduct = this._product?.id === payload?.id;
    const selectedImage = sameProduct ? this.mainImg : undefined;
    if (!sameProduct) {
      this.productRevision++;
      this.photoError = '';
      this.photoStatus = '';
    }
    this._product = payload;
    this.mainImg = payload?.images?.find((image) => this.sameImage(image, selectedImage)) || this.primaryImage;
  }

  @Output() productChange = new EventEmitter<Product>();

  private _product: Product | undefined;

  get product() {
    return this._product;
  }

  mainImg: Image | undefined;

  uploading = false;

  removing = false;

  settingPrimary = false;

  confirmingRemoval = false;

  @ViewChild('deletePhotoDialog', { static: true }) deletePhotoDialog!: TemplateRef<unknown>;

  photoError = '';

  photoStatus = '';

  private productRevision = 0;

  private destroyed = false;

  private readonly destroy$ = new Subject<void>();

  get busy() {
    return this.uploading || this.removing || this.confirmingRemoval || this.settingPrimary;
  }

  get primaryImage(): Image | undefined {
    const images = this.product?.images || [];
    return images.find((image) => image.isPrimary === true) || images[0];
  }

  get selectedPhotoNumber(): number {
    return (this.product?.images || []).findIndex((image) => this.sameImage(image, this.mainImg)) + 1;
  }

  isPrimary(image: Image | undefined): boolean {
    return this.sameImage(image, this.primaryImage);
  }

  isSelected(image: Image): boolean {
    return this.sameImage(image, this.mainImg);
  }

  private sameImage(first: Image | undefined, second: Image | undefined): boolean {
    if (!first || !second) return false;
    return first.id && second.id ? first.id === second.id : first.name === second.name;
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  select(image: Image) {
    this.mainImg = image;
  }

  async setPrimary(image: Image | undefined = this.mainImg) {
    if (!this.product || !image || this.busy || this.isPrimary(image)) return;
    if (!this.product.images?.some((current) => this.sameImage(current, image))) return;
    if (!Number.isInteger(image.id) || image.id <= 0) {
      this.photoError = 'Не удалось определить фото. Обновите карточку и попробуйте ещё раз';
      return;
    }
    const productId = this.product.id;
    const revision = this.productRevision;
    this.settingPrimary = true;
    this.photoError = '';
    this.photoStatus = '';
    try {
      const product = await firstValueFrom(this.productService.setPrimaryImage(productId, image.id));
      if (!this.isCurrentProduct(productId, revision)) return;
      this.product = product;
      this.productChange.emit(product);
      this.photoStatus = 'Основное фото сохранено';
    } catch (error) {
      if (this.isCurrentProduct(productId, revision)) {
        this.photoError = 'Не удалось изменить основное фото. Попробуйте ещё раз';
      }
    } finally {
      this.settingPrimary = false;
    }
  }

  openPhotoViewer() {
    const images = (this.product?.images || [])
      .map((image) => this.getImageUrl(image))
      .filter((url): url is string => Boolean(url));
    if (!images.length) return;
    const selectedUrl = this.getImageUrl(this.mainImg);
    this.dialog.open<PartPhotoViewerDialogComponent, PartPhotoViewerData>(
      PartPhotoViewerDialogComponent,
      {
        data: {
          images,
          index: Math.max(0, images.findIndex((url) => url === selectedUrl)),
          title: this.product?.description || 'Фото товара'
        },
        panelClass: 'part-photo-viewer-panel',
        width: '96vw',
        maxWidth: '96vw',
        height: '92vh',
        autoFocus: 'first-tabbable',
        restoreFocus: true
      }
    );
  }

  async uploadFromInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.product || this.busy) return;
    this.photoError = '';
    this.photoStatus = '';
    const imageType = file.type.toLowerCase().startsWith('image/');
    const imageExtension = /\.(jpe?g|png|webp|gif|avif|bmp|svg)$/i.test(file.name);
    if (!(imageType || (!file.type && imageExtension)) || file.size === 0) {
      this.photoError = 'Выберите непустой файл изображения: JPG, PNG, WebP или другой формат фото';
      return;
    }
    const productId = this.product.id;
    const revision = this.productRevision;
    const previousNames = new Set((this.product.images || []).map((image) => image.name));
    this.uploading = true;
    try {
      const uploaded = await this.productService.uploadImage(file, productId);
      const product = await firstValueFrom(uploaded);
      if (!this.isCurrentProduct(productId, revision)) return;
      this.product = product;
      this.mainImg = product.images?.find((image) => !previousNames.has(image.name)) || this.mainImg;
      this.productChange.emit(product);
      this.photoStatus = 'Фото добавлено';
    } catch (error: any) {
      if (!this.isCurrentProduct(productId, revision)) return;
      this.photoError = error?.status === 413
        ? 'Файл слишком большой. Выберите изображение меньшего размера'
        : 'Не удалось загрузить фото. Попробуйте ещё раз';
    } finally {
      this.uploading = false;
    }
  }

  private isCurrentProduct(id: number, revision: number) {
    return !this.destroyed && this.product?.id === id && this.productRevision === revision;
  }

  getImageUrl(image: Image | undefined): string | null {
    const imageName = String(image?.name || '').trim();
    if (/^(https?:|data:image\/)/i.test(imageName)) {
      return imageName;
    }

    if (imageName && !this.isRemoteImageReferenceName(imageName)) {
      return `${environment.BACKEND}/image/${imageName}`;
    }

    const directUrl = String(image?.url || '').trim();
    if (/^(https?:|data:image\/)/i.test(directUrl)) {
      return directUrl;
    }

    return imageName ? `${environment.BACKEND}/image/${imageName}` : null;
  }

  private isRemoteImageReferenceName(imageName?: string | null): boolean {
    return String(imageName || '').startsWith('supplier-remote-');
  }

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger | undefined;

  menuTopLeftPosition = { x: '0', y: '0' };
  onRightClick(event: MouseEvent, item: Image | undefined) {
    if (!this.matMenuTrigger) return;
    event.preventDefault();
    event.stopPropagation();
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';
    this.matMenuTrigger.menuData = { item: item };
    this.matMenuTrigger.openMenu();
  }
  async remove(image: Image | undefined) {
    if (!this.product || !image || this.busy) return;
    if (!this.product.images?.some((current) => this.sameImage(current, image))) return;
    if (!Number.isInteger(image.id) || image.id <= 0) {
      this.photoError = 'Не удалось определить фото. Обновите карточку и попробуйте ещё раз';
      return;
    }
    const productId = this.product.id;
    const revision = this.productRevision;
    this.removing = true;
    this.photoError = '';
    this.photoStatus = '';
    try {
      const product = await firstValueFrom(this.productService.removeProductImage(productId, image.id));
      if (!this.isCurrentProduct(productId, revision)) return;
      this.product = product;
      this.productChange.emit(product);
      this.photoStatus = 'Фото удалено';
    } catch (error) {
      if (this.isCurrentProduct(productId, revision)) this.photoError = 'Не удалось удалить фото';
    } finally {
      this.removing = false;
    }
  }
  requestRemove(image: Image | undefined) {
    if (!this.product || !image || this.busy) return;
    if (!this.product.images?.some((current) => this.sameImage(current, image))) return;
    const productId = this.product.id;
    const revision = this.productRevision;
    this.confirmingRemoval = true;
    let dialogRef: MatDialogRef<unknown, boolean>;
    dialogRef = this.dialog.open(this.deletePhotoDialog, {
      data: {
        url: this.getImageUrl(image),
        close: (confirmed: boolean) => dialogRef.close(confirmed)
      },
      width: '360px',
      maxWidth: '92vw',
      restoreFocus: true
    });
    dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe((confirmed) => {
      this.confirmingRemoval = false;
      if (confirmed === true && this.isCurrentProduct(productId, revision)) {
        void this.remove(image);
      }
    });
  }
  addImageFromUrl() {
    if (!this.product || this.busy) return;
    const productId = this.product.id;
    const revision = this.productRevision;
    const dialogRef = this.dialog.open(AddImageComponent);
    dialogRef.componentInstance.product = this.product;
    dialogRef.componentInstance.doneEvent
      .pipe(take(1), takeUntil(dialogRef.afterClosed()), takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data[0] && this.isCurrentProduct(productId, revision)) {
          this.product = data[0];
          this.productChange.emit(data[0]);
          this.photoStatus = 'Фото добавлено';
        }
        dialogRef.close();
      });
  }
}
